// src/pages/LectureVersionHistory.js
import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import PageContainer from "../components/Layout/PageContainer";
import SeoHead from "../components/SeoHead";
import { useLanguage } from "../i18n/LanguageContext";
import LectureFaqMenu from "../components/LectureFaqMenu";
import "../styles/lectureUpload.css";

function detectUiLocale(navUploadLabel) {
  if (!navUploadLabel) return "en";
  if (navUploadLabel.includes("上传")) return "zh-Hans";
  if (/[\u4e00-\u9fff]/.test(navUploadLabel)) return "zh-Hant";
  return "en";
}

const LectureVersionHistory = () => {
  const { t } = useLanguage();
  const navUploadLabel = t ? t("navUpload") : "Upload";
  const uiLocale = detectUiLocale(navUploadLabel);

  const strings = useMemo(() => {
    return {
      /* ---------------------- English ---------------------- */
      en: {
        headTitle: "STT Lecture Service — Version History (Beta)",
        headDesc:
          "Major user-facing changes for the Lecture STT Service during Beta: features, UI updates, and upcoming plans.",
        pageTitle: "Version & Updates",
        intro:
          "This page lists major user-facing features and changes for the Lecture STT (speech-to-text) service. Dates are approximate and may be adjusted as the Beta evolves.",
        lastUpdated: "Last updated: 02 Dec 2025 (Beta)",
        status: "📌 Status: Beta (pre-public). Features and limits may change.",

        // Left menu
        faqMenuTitle: "STT FAQ Menu",
        faqMenuHowToUse: "How to Use",
        faqMenuTips: "Recording & Upload Tips",
        faqMenuLanguage: "Supported Languages",
        faqMenuPrivacy: "Privacy & Data",
        faqMenuVersion: "Version & Updates",
        faqMenuSupport: "Support / Contact",
        faqMenuAll: "View all STT FAQs",

        // --- Version blocks ---
        v1Title: "🚀 Phase 1 Beta Launch — 2025-12",
        v1Items: [
          <>
            <strong>Lecture upload flow</strong>: upload your own recordings
            (phone, Zoom, local files) from the Upload page and track progress
            on the Status page.
          </>,
          <>
            <strong>Google sign-in</strong>: use Google sign-in to keep your
            lectures and transcripts tied to your account.
          </>,
          <>
            <strong>Auto / manual language</strong>: basic language selection
            with “Auto” detection and manual locale choices for common lecture
            languages.
          </>,
          <>
            <strong>Batch STT processing</strong>: queue-based processing with
            background jobs; larger files may take longer but will resume even
            if you close the page.
          </>,
          <>
            <strong>Initial FAQ set</strong>: How-to-Use guide, Privacy, Terms
            of Service and Version page for Lecture STT.
          </>,
        ],

        v2Title: "🧩 UI & FAQ Refinement — 2025-12",
        v2Items: [
          <>
            <strong>How-to-Use layout</strong>: two-column layout with FAQ menu
            on the left, matching the Upload page style.
          </>,
          <>
            <strong>Multilingual FAQ</strong>: English, Traditional Chinese and
            Simplified Chinese versions of key FAQ pages.
          </>,
          <>
            <strong>Lecture status improvements</strong>: clearer error text and
            links between Upload / Status / FAQ pages.
          </>,
        ],

        futureTitle: "🛠 Planned / In Progress (Preview)",
        futureNote:
          "The following items are plans and may change. Exact dates and details will be announced later.",
        futureItems: [
          <>
            <strong>Recording extension improvements</strong>: smoother
            integration with the Chrome extension for lecture recording
            (including clearer indicators and tips).
          </>,
          <>
            <strong>Partial / segmented uploads</strong>: exploring options to
            handle long lectures more robustly (for example segmented or staged
            uploads).
          </>,
          <>
            <strong>More language presets</strong>: additional presets and
            examples for common university subjects and lecture languages.
          </>,
        ],
      },

      /* ------------------- Traditional Chinese ------------------- */
      "zh-Hant": {
        headTitle: "STT 語音轉筆記 — 版本與更新記錄（Beta）",
        headDesc:
          "整理 STT 語音轉筆記在 Beta 階段的主要功能推出、介面更新與未來計畫。",
        pageTitle: "版本與更新記錄",
        intro:
          "本頁列出 STT 語音轉筆記（Lecture STT）在 Beta 階段的主要功能與改動。日期為預計時間，實際推出仍可能調整。",
        lastUpdated: '最後更新：2025 年 12 月 2 日（Beta）',
        status: "📌 狀態：Beta（預備公開），功能與限制可能持續調整。",

        faqMenuTitle: "STT 常見問題選單",
        faqMenuHowToUse: "如何使用",
        faqMenuTips: "錄音與上傳小貼士",
        faqMenuLanguage: "支援語言",
        faqMenuPrivacy: "隱私與資料安全",
        faqMenuVersion: "版本與更新記錄",
        faqMenuSupport: '支援／聯繫我們',
        faqMenuAll: "查看全部 STT 常見問題",

        v1Title: "🚀 Phase 1 Beta 上線 — 2025-12",
        v1Items: [
          <>
            <strong>講座上傳流程</strong>：可在「上傳 Upload」頁面上傳手機錄音、Zoom
            檔案等，並在「狀態 Status」頁查看處理進度。
          </>,
          <>
            <strong>Google 登入</strong>：以 Google 帳號登入，讓你的講座與逐字稿集中在同一帳號下管理。
          </>,
          <>
            <strong>自動／手動語言選擇</strong>：支援 Auto
            自動偵測與常用語言的手動選擇，適合一般課堂與會議錄音。
          </>,
          <>
            <strong>批次語音轉文字</strong>：背景佇列處理，大型檔案可能較慢，但即使關閉頁面，工作仍會在伺服器完成。
          </>,
          <>
            <strong>初版 FAQ</strong>：包含「如何使用」、隱私權、服務條款與版本記錄等 STT 專屬說明頁面。
          </>,
        ],

        v2Title: "🧩 介面與 FAQ 微調 — 2025-12",
        v2Items: [
          <>
            <strong>使用說明版面</strong>：左側 FAQ 選單、右側內容的兩欄式設計，與上傳頁面風格一致。
          </>,
          <>
            <strong>多語 FAQ</strong>：提供英文、繁體中文與簡體中文版本的核心常見問題頁面。
          </>,
          <>
            <strong>狀態頁改善</strong>：錯誤提示與連結更清楚，方便由狀態頁直接前往 FAQ 或重新上傳。
          </>,
        ],

        futureTitle: "🛠 計畫中／開發中功能（預告）",
        futureNote:
          "以下為預計方向，實際內容與時間可能因技術與資源情況而調整，日後會再公佈細節。",
        futureItems: [
          <>
            <strong>錄音擴充功能優化</strong>：改善 Chrome 擴充小工具體驗，例如錄音狀態顯示與小貼士說明等。
          </>,
          <>
            <strong>長錄音分段處理</strong>：評估長時間講座以分段或階段性上傳方式處理，提升穩定度與成功率。
          </>,
          <>
            <strong>更多語言預設</strong>：加入更多常見學科與語言組合的預設與範例設定，方便快速選擇。
          </>,
        ],
      },

      /* ------------------- Simplified Chinese ------------------- */
      "zh-Hans": {
        headTitle: "STT 语音转笔记 — 版本与更新记录（Beta）",
        headDesc:
          "整理 STT 语音转笔记在 Beta 阶段的主要功能发布、界面更新与后续计划。",
        pageTitle: "版本与更新记录",
        intro:
          "本页列出 STT 语音转笔记（Lecture STT）在 Beta 阶段的主要功能与改动，日期为预估时间，可能会调整。",
        lastUpdated: '最后更新：2025 年 12 月 2 日（Beta）',
        status: "📌 状态：Beta（预公开），功能与限制可能持续调整。",

        faqMenuTitle: "STT 常见问题菜单",
        faqMenuHowToUse: "如何使用",
        faqMenuTips: "录音与上传小贴士",
        faqMenuLanguage: "支持语言",
        faqMenuPrivacy: "隐私与数据安全",
        faqMenuVersion: "版本与更新记录",
        faqMenuSupport: '客户／联系我们',
        faqMenuAll: "查看全部 STT 常见问题",

        v1Title: "🚀 Phase 1 Beta 上线 — 2025-12",
        v1Items: [
          <>
            <strong>讲座上传流程</strong>：在「上传 Upload」页面上传手机录音、Zoom
            文件等，在「状态 Status」页面查看处理进度。
          </>,
          <>
            <strong>Google 登录</strong>：使用 Google 账号登录，将讲座与逐字稿统一管理。
          </>,
          <>
            <strong>自动／手动语言选择</strong>：提供 Auto
            自动检测与常用语言的手动选择，适合一般课程与会议录音。
          </>,
          <>
            <strong>批量 STT 处理</strong>：基于队列的后台处理，大文件可能较慢，但关闭网页后任务仍会在服务器继续执行。
          </>,
          <>
            <strong>首版 FAQ</strong>：新增「如何使用」、隐私、服务条款与版本记录等 STT 专用说明页面。
          </>,
        ],

        v2Title: "🧩 界面与 FAQ 微调 — 2025-12",
        v2Items: [
          <>
            <strong>使用说明布局</strong>：左侧 FAQ 菜单、右侧内容的两栏布局，与上传页面风格一致。
          </>,
          <>
            <strong>多语言 FAQ</strong>：提供英文、繁体中文与简体中文版本的核心常见问题。
          </>,
          <>
            <strong>状态页优化</strong>：错误提示与链接更清晰，方便从状态页跳转到 FAQ 或重新上传。
          </>,
        ],

        futureTitle: "🛠 计划中／开发中（预告）",
        futureNote:
          "以下为规划方向，具体内容与时间可能根据技术与资源情况调整，后续会再公告。",
        futureItems: [
          <>
            <strong>录音扩展优化</strong>：改进 Chrome 扩展的小窗体验，例如录音状态提示与操作说明等。
          </>,
          <>
            <strong>长录音分段处理</strong>：评估对长时间讲座采用分段／阶段上传的方式，提高稳定性和成功率。
          </>,
          <>
            <strong>更多语言预设</strong>：增加更多常见学科与语言组合的预设和示例设置，方便快速选择。
          </>,
        ],
      },
    }[uiLocale];
  }, [uiLocale]);

  const url = "https://stt.aittsservice.online/lecture/faq/version";

  const breadcrumbs = [
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: uiLocale === "en" ? "STT Lecture Service" : "STT 語音轉筆記服務",
          item: "https://stt.aittsservice.online/",
        },
        {
          "@type": "ListItem",
          position: 2,
          name: uiLocale === "en" ? "FAQ" : "常見問題",
          item: "https://stt.aittsservice.online/lecture/faq",
        },
        {
          "@type": "ListItem",
          position: 3,
          name: strings.pageTitle,
          item: url,
        },
      ],
    },
  ];

  return (
    <PageContainer>
      <SeoHead
        title={strings.headTitle}
        description={strings.headDesc}
        url={url}
        structuredData={breadcrumbs}
      />

      {/* Hero */}
      <div className="card upload-hero">
      <div className="card-heading">
        <h1 className="card-title">{strings.pageTitle}</h1>
      </div>
      <p>{strings.intro}</p>
      <p style={{ fontSize: 12, marginTop: 4 }}>{strings.lastUpdated}</p>
      <p className="text-sm mt-1">{strings.status}</p>
    </div>

      {/* Layout: same as How-to-Use */}
      <div className="card upload-card upload-faq-card">
        <div className="upload-layout upload-layout--faq howto-grid">
          {/* Left menu */}
          <LectureFaqMenu active="version" />


          {/* Right: version content */}
          <main className="upload-main upload-main--faq howto-main">
            {/* Phase 1 Beta */}
            <section className="faq-section">
              <h2>{strings.v1Title}</h2>
              <ul>
                {strings.v1Items.map((item, idx) => (
                  <li key={idx}>{item}</li>
                ))}
              </ul>
            </section>

            {/* UI & FAQ refinement */}
            <section className="faq-section">
              <h2>{strings.v2Title}</h2>
              <ul>
                {strings.v2Items.map((item, idx) => (
                  <li key={idx}>{item}</li>
                ))}
              </ul>
            </section>

            {/* Planned / In progress */}
            <section className="faq-section">
              <h2>{strings.futureTitle}</h2>
              <p>{strings.futureNote}</p>
              <ul>
                {strings.futureItems.map((item, idx) => (
                  <li key={idx}>{item}</li>
                ))}
              </ul>
            </section>
          </main>
        </div>
      </div>
    </PageContainer>
  );
};

export default LectureVersionHistory;
